import { useEffect, useState } from "react";
import axios from "axios";
import {
  Loader2, Plus, Pencil, Trash2, Save, ToggleLeft, ToggleRight,
  Package, Layers, Euro, X, Copy, FileText,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

function ItemRow({ item, type, onUpdate, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...item });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await onUpdate(item.id, form);
      setEditing(false);
    } finally { setSaving(false); }
  };

  const priceField = type === "base" ? "setup_price" : "monthly_price";
  const priceLabel = type === "base" ? "Setup (EUR)" : "Mensuel (EUR/mois)";

  if (editing) {
    return (
      <div className="bg-[#0B0F19] border border-[#0099CC]/30 rounded-xl p-4 space-y-3" data-testid={`edit-${type}-${item.id}`}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">Nom</Label>
            <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
              className="bg-[#151B2B] border-[#2A3441] text-white h-9" />
          </div>
          <div className="space-y-1">
            <Label className="text-[#94A3B8] text-xs">{priceLabel}</Label>
            <Input type="number" step="0.01" min="0" value={form[priceField]}
              onChange={e => setForm(f => ({...f, [priceField]: parseFloat(e.target.value) || 0}))}
              className="bg-[#151B2B] border-[#2A3441] text-white h-9" />
          </div>
          <div className="flex items-end gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#10B981] text-white h-9">
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Sauver
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="border-[#2A3441] text-[#94A3B8] h-9">
              <X className="h-3 w-3" />
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4 flex items-center gap-4 hover:border-[#2A3441]/80 transition-colors"
      data-testid={`${type}-row-${item.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <p className="text-white font-medium">{item.name}</p>
          {!item.is_active && <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-0 text-xs">Desactive</Badge>}
        </div>
      </div>
      <p className="text-[#0099CC] font-bold whitespace-nowrap">
        {item[priceField]?.toFixed(0)} EUR{type === "option" ? "/mois" : ""}
      </p>
      <div className="flex items-center gap-1 shrink-0">
        <Button variant="ghost" size="sm" onClick={() => onUpdate(item.id, { is_active: !item.is_active })}
          className={item.is_active ? "text-[#10B981] hover:text-[#10B981]" : "text-[#64748b] hover:text-[#64748b]"}
          data-testid={`toggle-${type}-${item.id}`}>
          {item.is_active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => setEditing(true)}
          className="text-[#F59E0B] hover:text-[#F59E0B]" data-testid={`edit-${type}-${item.id}`}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(item.id)}
          className="text-[#EF4444] hover:text-[#EF4444]" data-testid={`delete-${type}-${item.id}`}>
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function AddForm({ type, onAdd }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!name.trim()) { toast.error("Nom requis"); return; }
    setSaving(true);
    try {
      const payload = type === "base"
        ? { name, setup_price: price }
        : { name, monthly_price: price };
      await onAdd(payload);
      setName(""); setPrice(0); setOpen(false);
    } finally { setSaving(false); }
  };

  if (!open) {
    return (
      <Button size="sm" onClick={() => setOpen(true)}
        className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
        data-testid={`add-${type}-btn`}>
        <Plus className="h-3.5 w-3.5 mr-1.5" />
        Ajouter {type === "base" ? "une base" : "une option"}
      </Button>
    );
  }

  const priceLabel = type === "base" ? "Prix setup (EUR)" : "Prix mensuel (EUR/mois)";

  return (
    <div className="bg-[#0B0F19] border border-[#0099CC]/30 rounded-xl p-4" data-testid={`add-${type}-form`}>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-[#94A3B8] text-xs">Nom</Label>
          <Input value={name} onChange={e => setName(e.target.value)}
            placeholder={type === "base" ? "Ex: Refonte site web" : "Ex: Newsletter"}
            className="bg-[#151B2B] border-[#2A3441] text-white h-9" data-testid={`add-${type}-name`} />
        </div>
        <div className="space-y-1">
          <Label className="text-[#94A3B8] text-xs">{priceLabel}</Label>
          <Input type="number" step="0.01" min="0" value={price}
            onChange={e => setPrice(parseFloat(e.target.value) || 0)}
            className="bg-[#151B2B] border-[#2A3441] text-white h-9" data-testid={`add-${type}-price`} />
        </div>
        <div className="flex items-end gap-2">
          <Button size="sm" onClick={submit} disabled={saving} className="bg-[#10B981] text-white h-9" data-testid={`save-${type}-btn`}>
            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}Ajouter
          </Button>
          <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="border-[#2A3441] text-[#94A3B8] h-9">
            <X className="h-3 w-3" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function TemplatesSection({ bases, options }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: "", description: "", base_id: "", option_ids: [] });

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/proposal-templates`);
      setTemplates(res.data);
    } catch { toast.error("Erreur chargement modeles"); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchTemplates(); }, []);

  const resetForm = () => { setForm({ name: "", description: "", base_id: "", option_ids: [] }); setCreating(false); setEditingId(null); };

  const save = async () => {
    if (!form.name || !form.base_id) { toast.error("Nom et base requis"); return; }
    try {
      if (editingId) {
        await axios.put(`${API}/proposal-templates/${editingId}`, form);
        toast.success("Modele mis a jour");
      } else {
        await axios.post(`${API}/proposal-templates`, form);
        toast.success("Modele cree");
      }
      resetForm(); fetchTemplates();
    } catch { toast.error("Erreur"); }
  };

  const duplicate = async (id) => {
    await axios.post(`${API}/proposal-templates/${id}/duplicate`);
    toast.success("Modele duplique"); fetchTemplates();
  };
  const remove = async (id) => {
    await axios.delete(`${API}/proposal-templates/${id}`);
    toast.success("Modele supprime"); fetchTemplates();
  };
  const toggleActive = async (id, isActive) => {
    await axios.put(`${API}/proposal-templates/${id}`, { is_active: !isActive });
    fetchTemplates();
  };
  const startEdit = (tpl) => {
    setForm({ name: tpl.name, description: tpl.description || "", base_id: tpl.base_id, option_ids: tpl.option_ids || [] });
    setEditingId(tpl.id); setCreating(true);
  };
  const toggleOpt = (optId) => {
    setForm(f => ({...f, option_ids: f.option_ids.includes(optId) ? f.option_ids.filter(id => id !== optId) : [...f.option_ids, optId]}));
  };

  return (
    <div className="space-y-4" data-testid="templates-section">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-[#8B5CF6]" />
          <h2 className="text-lg font-semibold text-white">Modeles de propositions</h2>
          <Badge className="bg-[#8B5CF6]/10 text-[#8B5CF6] border-0 text-xs">{templates.length}</Badge>
        </div>
        {!creating && (
          <Button size="sm" onClick={() => { resetForm(); setCreating(true); }} className="bg-[#8B5CF6] hover:bg-[#7c3aed] text-white" data-testid="add-template-btn">
            <Plus className="h-3.5 w-3.5 mr-1.5" />Nouveau modele
          </Button>
        )}
      </div>
      <p className="text-[#64748b] text-sm">Modeles pre-configures pour creer des propositions en 1 clic</p>

      {creating && (
        <div className="bg-[#0B0F19] border border-[#8B5CF6]/30 rounded-xl p-5 space-y-4" data-testid="template-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Nom du modele</Label>
              <Input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))}
                placeholder="Ex: Pack Site Vitrine" className="bg-[#151B2B] border-[#2A3441] text-white h-9" data-testid="template-name-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-[#94A3B8] text-xs">Description</Label>
              <Input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))}
                placeholder="Description optionnelle" className="bg-[#151B2B] border-[#2A3441] text-white h-9" />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8] text-xs">Base de projet</Label>
            <div className="flex gap-2 flex-wrap">
              {bases.filter(b => b.is_active).map(b => (
                <button key={b.id} type="button" onClick={() => setForm(f => ({...f, base_id: b.id}))}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${form.base_id === b.id ? "border-[#0099CC] bg-[#0099CC]/10 text-[#0099CC]" : "border-[#2A3441] text-[#94A3B8]"}`}>
                  {b.name} ({b.setup_price}€)
                </button>
              ))}
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-[#94A3B8] text-xs">Options incluses</Label>
            <div className="flex gap-2 flex-wrap">
              {options.filter(o => o.is_active).map(o => (
                <button key={o.id} type="button" onClick={() => toggleOpt(o.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs border transition-all ${form.option_ids.includes(o.id) ? "border-[#10B981] bg-[#10B981]/10 text-[#10B981]" : "border-[#2A3441] text-[#94A3B8]"}`}>
                  {o.name} (+{o.monthly_price}€/mois)
                </button>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} className="bg-[#10B981] text-white h-8" data-testid="save-template-btn">
              <Save className="h-3 w-3 mr-1" />{editingId ? "Mettre a jour" : "Creer"}
            </Button>
            <Button size="sm" variant="outline" onClick={resetForm} className="border-[#2A3441] text-[#94A3B8] h-8">Annuler</Button>
          </div>
        </div>
      )}

      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-[#8B5CF6] mx-auto" />
      ) : (
        <div className="space-y-2">
          {templates.map(tpl => {
            const base = bases.find(b => b.id === tpl.base_id);
            const basePrice = base?.setup_price || 0;
            const monthlyTotal = (tpl.option_ids || []).reduce((sum, oid) => {
              const o = options.find(op => op.id === oid);
              return sum + (o?.monthly_price || 0);
            }, 0);
            return (
              <div key={tpl.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4" data-testid={`template-row-${tpl.id}`}>
                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-white font-medium">{tpl.name}</p>
                      {!tpl.is_active && <Badge className="bg-[#EF4444]/10 text-[#EF4444] border-0 text-xs">Desactive</Badge>}
                    </div>
                    {tpl.description && <p className="text-[#64748b] text-xs mt-0.5">{tpl.description}</p>}
                    <div className="flex gap-2 mt-1.5 flex-wrap">
                      <Badge className="bg-[#0099CC]/10 text-[#0099CC] border-0 text-xs">{tpl.base_name}</Badge>
                      {(tpl.option_names || []).map((n, i) => <Badge key={i} className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs">{n}</Badge>)}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white font-bold text-sm">{basePrice.toFixed(0)} EUR</p>
                    <p className="text-[#10B981] text-xs">+{monthlyTotal.toFixed(0)} EUR/mois</p>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button variant="ghost" size="sm" onClick={() => toggleActive(tpl.id, tpl.is_active)}
                      className={tpl.is_active ? "text-[#10B981]" : "text-[#64748b]"} data-testid={`toggle-template-${tpl.id}`}>
                      {tpl.is_active ? <ToggleRight className="h-5 w-5" /> : <ToggleLeft className="h-5 w-5" />}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(tpl)} className="text-[#F59E0B]">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => duplicate(tpl.id)} className="text-[#8B5CF6]" data-testid={`duplicate-template-${tpl.id}`}>
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(tpl.id)} className="text-[#EF4444]">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function ProposalSettingsPage() {
  const [bases, setBases] = useState([]);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [bRes, oRes] = await Promise.all([
        axios.get(`${API}/proposal-bases`),
        axios.get(`${API}/proposal-options`),
      ]);
      setBases(bRes.data);
      setOptions(oRes.data);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const addBase = async (payload) => {
    await axios.post(`${API}/proposal-bases`, payload);
    toast.success("Base ajoutee");
    fetchAll();
  };
  const updateBase = async (id, payload) => {
    await axios.put(`${API}/proposal-bases/${id}`, payload);
    toast.success("Base mise a jour");
    fetchAll();
  };
  const deleteBase = async (id) => {
    await axios.delete(`${API}/proposal-bases/${id}`);
    toast.success("Base supprimee");
    fetchAll();
  };

  const addOption = async (payload) => {
    await axios.post(`${API}/proposal-options`, payload);
    toast.success("Option ajoutee");
    fetchAll();
  };
  const updateOption = async (id, payload) => {
    await axios.put(`${API}/proposal-options/${id}`, payload);
    toast.success("Option mise a jour");
    fetchAll();
  };
  const deleteOption = async (id) => {
    await axios.delete(`${API}/proposal-options/${id}`);
    toast.success("Option supprimee");
    fetchAll();
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="proposal-settings-page">
      <div>
        <h1 className="text-2xl font-bold text-white">Parametrages Propositions</h1>
        <p className="text-[#94A3B8] mt-1">Configurez les bases de projet et les options mensuelles</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : (
        <>
          {/* Bases section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="h-5 w-5 text-[#0099CC]" />
                <h2 className="text-lg font-semibold text-white">Bases de projet</h2>
                <Badge className="bg-[#0099CC]/10 text-[#0099CC] border-0 text-xs">{bases.length}</Badge>
              </div>
              <AddForm type="base" onAdd={addBase} />
            </div>
            <p className="text-[#64748b] text-sm">Le prix de base constitue le setup principal du projet</p>
            <div className="space-y-2">
              {bases.map(b => (
                <ItemRow key={b.id} item={b} type="base" onUpdate={updateBase} onDelete={deleteBase} />
              ))}
            </div>
          </div>

          {/* Options section */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-[#10B981]" />
                <h2 className="text-lg font-semibold text-white">Options mensuelles</h2>
                <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs">{options.length}</Badge>
              </div>
              <AddForm type="option" onAdd={addOption} />
            </div>
            <p className="text-[#64748b] text-sm">Les options s'ajoutent automatiquement au mensuel de la proposition</p>
            <div className="space-y-2">
              {options.map(o => (
                <ItemRow key={o.id} item={o} type="option" onUpdate={updateOption} onDelete={deleteOption} />
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="bg-[#0099CC]/5 border border-[#0099CC]/20 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-3">
              <Euro className="h-4 w-4 text-[#0099CC]" />
              <h3 className="text-white font-semibold text-sm">Apercu des prix</h3>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
              <div>
                <p className="text-[#64748b] mb-2">Bases de projet (setup)</p>
                {bases.filter(b => b.is_active).map(b => (
                  <div key={b.id} className="flex justify-between py-1">
                    <span className="text-[#94A3B8]">{b.name}</span>
                    <span className="text-white font-medium">{b.setup_price?.toFixed(0)} EUR</span>
                  </div>
                ))}
              </div>
              <div>
                <p className="text-[#64748b] mb-2">Options mensuelles</p>
                {options.filter(o => o.is_active).map(o => (
                  <div key={o.id} className="flex justify-between py-1">
                    <span className="text-[#94A3B8]">{o.name}</span>
                    <span className="text-white font-medium">+{o.monthly_price?.toFixed(0)} EUR/mois</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Templates section */}
          <TemplatesSection bases={bases} options={options} />
        </>
      )}
    </div>
  );
}
